import { useStyles } from "./MyRCAs.styles";
import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import NoData from "components/NoData/NoData.view";
import { MainTable } from "components/MainTable";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import useMyRCAsColumns from "./useMyRCAsColumns/useMyRCAsColumns";
import { useNavigate } from "react-router-dom";
import { RCALibraryEntity } from "entities/RCA/RCALibraryEntity/RCALibraryEntity";
import { useContext, useMemo, useState } from "react";
import { UserContext } from "contexts/UserContext/UserContext";
import { useTableOperations } from "components/MainTable/hooks/useTableOperations";
import { CORPORATE_BACKOFFICE_PATHS } from "constants/constants";
import { useTranslation } from "react-i18next";
import { Card, Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { IMyRCAsFilters } from "./MyRCAsInterfaces";
import MainCardButtonAction from "components/MainCardButtonAction";
import useRolesPermissions from "hooks/useRolesPermissions";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CreateEditManualRCAModal from "pages/Corporate/RCA/RCALibrary/components/RCALibraryDetail/components/CreateEditManualRCAModal/CreateEditManualRCAModal";

const MyRCAs = () => {
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const { t } = useTranslation();
    const { user_id } = useContext(UserContext);
    const { handleRequestSort, handleChangeRowsPerPage, handleChangePage, order, orderBy, page, rowsPerPage } =
        useTableOperations();
    const [filters, setFilters] = useState<IMyRCAsFilters>({ showCompleted: false });
    const [isCreateRCAModalOpen, setIsCreateRCAModalOpen] = useState<boolean>(false);
    const tableColumns = useMyRCAsColumns() as unknown as IMainTableColumn[];
    const navigate = useNavigate();
    const { RCA_PERMISSIONS } = useRolesPermissions();

    const queryTableOperationFilters = useMemo(
        () => ({
            order,
            orderBy,
            limit: rowsPerPage,
            offset: page * rowsPerPage,
        }),
        [order, orderBy, rowsPerPage, page],
    );
    const { loading, userRCAs, apolloQueryError, totalCount } = RCALibraryEntity.GetUserRCAs(
        queryTableOperationFilters,
        user_id!,
        filters,
    );
    return (
        <Card className={mainClasses.contentCard}>
            <div className={mainClasses.contentCardHeader}>
                <div className={classes.titleContainer}>
                    <span className={mainClasses.contentCardTitleCapitalized}>{t("rca.rca_dashboard.my_rcas")}</span>
                    <MainCardButtonAction
                        disabled={!RCA_PERMISSIONS?.dashboard.canCreateRca}
                        title={t("rca.rca_dashboard.add_rca")}
                        renderIcon={() => <AddCircleIcon />}
                        onClick={() => setIsCreateRCAModalOpen(true)}
                    />
                </div>
                <div className={mainClasses.contentCardActions}>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    value={true}
                                    color="info"
                                    onChange={(_, checked) => {
                                        setFilters({ ...filters, showCompleted: checked });
                                    }}
                                />
                            }
                            label={t("rca.rca_dashboard.show_completed")}
                        />
                    </FormGroup>
                </div>
            </div>
            <div className={mainClasses.contentCardContent}>
                {userRCAs && !userRCAs.length && (
                    <NoData className={mainClasses.contentNoData} title={t("data.no_data_short")} />
                )}
                {userRCAs && !!userRCAs.length && (
                    <MainTable
                        maxHeight={false}
                        customStyle={`${mainClasses.contentTable} ${classes.mainTable}`}
                        paperElevation={0}
                        paperCustomStyle={mainClasses.contentTablePaperMyDashboard}
                        columns={tableColumns}
                        tableCellCustomStyle={mainClasses.contentTableCell}
                        customStyleTypography={{ width: "100%" }}
                        rows={!loading && !apolloQueryError ? userRCAs : []}
                        loading={loading}
                        tableOperationsValues={{ order, orderBy, page, rowsPerPage }}
                        handleRequestSortParent={handleRequestSort}
                        handleChangeRowsPerPageParent={handleChangeRowsPerPage}
                        handleChangePageParent={handleChangePage}
                        pagination
                        count={totalCount}
                        querySorted={!!orderBy}
                        onRowClick={(row: { rca_id?: number }) => {
                            navigate(
                                `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_LIBRARY.ROOT}/${row.rca_id}`,
                                {
                                    state: {
                                        from: `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}`,
                                    },
                                },
                            );
                        }}
                    />
                )}
            </div>
            <CreateEditManualRCAModal
                open={isCreateRCAModalOpen}
                onClose={() => setIsCreateRCAModalOpen(false)}
                onAddRCA={async (rca) => {
                    navigate(
                        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_LIBRARY.ROOT}/${rca.rca_id}`,
                        {
                            state: {
                                from: `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.RCA.ROOT}/${CORPORATE_BACKOFFICE_PATHS.RCA.RCA_LIBRARY.ROOT}`,
                            },
                        },
                    );
                    setIsCreateRCAModalOpen(false);
                }}
            />
        </Card>
    );
};

export default MyRCAs;
